import React, { useState, useEffect } from 'react';
import { BookOpen, Star, StarOff, Trash2, MoreVertical, Filter, Search, SortAsc, SortDesc, Grid, List, Calendar, Settings, X } from 'lucide-react';
import { readerService, QueueItem } from '../../services/readerService';

interface EnhancedReadingQueueProps {
  userId: string;
  onReadArticle: (articleId: string) => void;
  onRemoveFromQueue: (articleId: string) => void;
  onUpdatePriority: (articleId: string, priority: QueueItem['priority']) => void;
  onStarArticle: (articleId: string, starred: boolean) => void;
  onArchiveArticle: (articleId: string) => void;
}

type ViewMode = 'list' | 'grid' | 'timeline';
type SortBy = 'added' | 'priority' | 'readTime' | 'author' | 'newsletter';
type SortOrder = 'asc' | 'desc';

const EnhancedReadingQueue: React.FC<EnhancedReadingQueueProps> = ({
  userId,
  onReadArticle,
  onRemoveFromQueue,
  onUpdatePriority,
  onStarArticle,
  onArchiveArticle
}) => {
  // Real service functions
  const handleRemoveFromQueue = async (articleId: string) => {
    try {
      await readerService.removeFromQueue(articleId);
      setQueueItems(prev => prev.filter(item => item.id !== articleId));
      onRemoveFromQueue(articleId);
    } catch (error) {
      console.error('Error removing from queue:', error);
    }
  };

  const handleUpdatePriority = async (articleId: string, priority: QueueItem['priority']) => {
    try {
      setQueueItems(prev => prev.map(item => 
        item.id === articleId ? { ...item, priority } : item
      ));
      onUpdatePriority(articleId, priority);
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleStarArticle = async (articleId: string, starred: boolean) => {
    try {
      setQueueItems(prev => prev.map(item => 
        item.id === articleId ? { ...item, isStarred: starred } : item
      ));
      onStarArticle(articleId, starred);
    } catch (error) {
      console.error('Error starring article:', error);
    }
  };

  const handleArchiveArticle = async (articleId: string) => {
    try {
      setQueueItems(prev => prev.map(item => 
        item.id === articleId ? { ...item, isArchived: true } : item
      ));
      onArchiveArticle(articleId);
    } catch (error) {
      console.error('Error archiving article:', error);
    }
  };
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortBy, setSortBy] = useState<SortBy>('added');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [showRead, setShowRead] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [autoQueue, setAutoQueue] = useState(true);
  const [maxQueueSize, setMaxQueueSize] = useState(50);

  // Load real data from reader service
  useEffect(() => {
    const loadQueueData = async () => {
      try {
        setLoading(true);
        const queueItems = await readerService.getReadingQueue(userId);
        setQueueItems(queueItems);
      } catch (error) {
        console.error('Error loading reading queue:', error);
        // Fallback to empty array if error
        setQueueItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadQueueData();
  }, [userId]);

  // Filter and sort items
  useEffect(() => {
    let filtered = [...queueItems];

    // Filter by read status
    if (!showRead) {
      filtered = filtered.filter(item => !item.isRead);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.newsletter.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by priority
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(item => item.priority === selectedPriority);
    }

    // Sort items
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'added':
          comparison = a.addedAt.getTime() - b.addedAt.getTime();
          break;
        case 'priority':
          const priorityOrder = { urgent: 3, normal: 2, low: 1 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case 'readTime':
          comparison = a.readTime - b.readTime;
          break;
        case 'author':
          comparison = a.author.localeCompare(b.author);
          break;
        case 'newsletter':
          comparison = a.newsletter.localeCompare(b.newsletter);
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredItems(filtered);
  }, [queueItems, searchQuery, selectedCategory, selectedPriority, showRead, sortBy, sortOrder]);

  const getPriorityColor = (priority: QueueItem['priority']) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      case 'normal': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'low': return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getPriorityIcon = (priority: QueueItem['priority']) => {
    switch (priority) {
      case 'urgent': return '🔴';
      case 'normal': return '🔵';
      case 'low': return '⚪';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const categories = ['all', ...Array.from(new Set(queueItems.map(item => item.category)))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reading Queue</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {filteredItems.length} articles • {filteredItems.filter(item => !item.isRead).length} unread
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Filters */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>

          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list' 
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'timeline' 
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="added">Added Date</option>
              <option value="priority">Priority</option>
              <option value="readTime">Read Time</option>
              <option value="author">Author</option>
              <option value="newsletter">Newsletter</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </button>
          </div>

          {/* Show Read Toggle */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showRead}
              onChange={(e) => setShowRead(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Show read</span>
          </label>
        </div>
      </div>

      {/* Queue Items */}
      <div className={`${
        viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          : viewMode === 'timeline'
          ? 'space-y-4'
          : 'space-y-4'
      }`}>
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-200 hover:shadow-lg ${
              item.isRead ? 'opacity-60' : ''
            }`}
          >
            {viewMode === 'grid' ? (
              // Grid View
              <div className="h-full flex flex-col">
                {item.imageUrl && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                      {getPriorityIcon(item.priority)} {item.priority}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleStarArticle(item.id, !item.isStarred)}
                        className={`p-1 rounded transition-colors ${
                          item.isStarred 
                            ? 'text-yellow-500' 
                            : 'text-gray-400 hover:text-yellow-500'
                        }`}
                      >
                        {item.isStarred ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {item.excerpt}
                  </p>
                  <div className="mt-auto">
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-3">
                      <span>{item.author}</span>
                      <span>{item.readTime} min</span>
                    </div>
                    <button
                      onClick={() => onReadArticle(item.id)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {item.isRead ? 'Re-read' : 'Read'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // List/Timeline View
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {item.imageUrl && (
                    <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                          {getPriorityIcon(item.priority)} {item.priority}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimeAgo(item.addedAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleStarArticle(item.id, !item.isStarred)}
                          className={`p-1 rounded transition-colors ${
                            item.isStarred 
                              ? 'text-yellow-500' 
                              : 'text-gray-400 hover:text-yellow-500'
                          }`}
                        >
                          {item.isStarred ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {item.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>{item.author}</span>
                        <span>•</span>
                        <span>{item.newsletter}</span>
                        <span>•</span>
                        <span>{item.readTime} min</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onReadArticle(item.id)}
                          className="bg-blue-600 text-white py-1 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          {item.isRead ? 'Re-read' : 'Read'}
                        </button>
                        <button
                          onClick={() => handleRemoveFromQueue(item.id)}
                          className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No articles found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all'
              ? 'Try adjusting your filters or search terms.'
              : 'Your reading queue is empty. Discover new newsletters to add to your queue.'}
          </p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Queue Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Auto-queue new articles</label>
                <button
                  onClick={() => setAutoQueue(!autoQueue)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoQueue ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoQueue ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Max queue size</label>
                <input
                  type="number"
                  value={maxQueueSize}
                  onChange={(e) => setMaxQueueSize(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedReadingQueue;
